import context, { Constants } from '@truckdown/systems';
import { PromotionModel, IPromotionManager } from '@truckdown/promotions';
import { defineComponent, ref, Ref, computed, onMounted, watch } from 'vue';
import dailyLimit from "../DailyLimit/DailyLimit.vue";
import removeFunds from "../RemoveFunds/RemoveFunds.vue";
import addFunds from "../AddFunds/AddFunds.vue";
import editPromotion from "../EditPromotion/EditPromotion.vue";
import recurringCharge from "../RecurringCharge/RecurringCharge.vue";
import approvePromotion from "../Approve/Approve.vue";
import { PromotionStatus, Listing } from '@truckdown/locations';
import removePromotion from '../RemovePromotion/RemovePromotion.vue';
import { ILocationQueryService, LocationQueryOptions, LocationModel, MeasureUnit } from '@truckdown/locations';
import { isCsrOrAdmin, showMessage } from '../../Utilities/utilities'

interface IProps {
    promotion: PromotionModel;
    listing: Listing;
    taxname: string;
    taxrate: number;
    isModal: boolean;
}

interface IData {
    showEditPromotion: Ref<boolean>,
    showAddFunds: Ref<boolean>,
    showDailyLimit: Ref<boolean>,
    showRecurringCharge: Ref<boolean>,
    showRemovePromotion: Ref<boolean>,
    showRemoveFunds: Ref<boolean>,
    locations: Ref<LocationModel[]>,
    showPromotionLocations: Ref<boolean>,
}

const getInitialData = function (): IData {
    return {
        showEditPromotion: ref<boolean>(false),
        showAddFunds: ref<boolean>(false),
        showDailyLimit: ref<boolean>(false),
        showRecurringCharge: ref<boolean>(false),
        showRemovePromotion: ref<boolean>(false),
        showRemoveFunds: ref<boolean>(false),
        locations: ref<LocationModel[]>([]),
        showPromotionLocations: ref<boolean>(false)
    };
}

const getComputed = function (props: IProps, data: IData) {
    return {
        showPendingEditOptions: computed(() => {
            if (isCsrOrAdmin()) {
                if (props.promotion.edit) {
                    return true;
                }
            }
            
            return false;
        }),
        status: computed(() => {
            var status = PromotionStatus[props.promotion.status];
            return status == 'PendingApproval' ? 'Pending Approval' : status;
        }),
        expiryDate: computed(() => {
            if (!props.promotion.expiry) {
                return "N/A";
            } else {
                let options: Intl.DateTimeFormatOptions = { timeZone: "UTC", year: "numeric", month: "numeric", day: "numeric" };
                return new Date(props.promotion.expiry).toLocaleDateString(undefined, options);
            }
        }),
        siteName: computed(() => {
            if (props.promotion.site == "truckdown") {
                return "TruckDown";
            } else if (props.promotion.site == "planrv") {
                return "PlanRV";
            } else {
                return props.promotion.site;
            }
        }),
        dailyBudgetDisplay: computed(() => {
            if (props.promotion.funds.dailyLimit > 0) {
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.promotion.funds.dailyLimit);
            } else {
                return 'N/A';
            }
        }),
        currentAmount: computed(() => {
            return props.promotion.funds.activeFunds + props.promotion.funds.availableFunds;
        }),
        currentBalance: computed(() => {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.promotion.funds.activeFunds + props.promotion.funds.availableFunds);
        }),
        todayBalance: computed(() => {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.promotion.funds.activeFunds);
        }),
        total: computed(() => {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.promotion.funds.totalFundsToDate);
        }),
        nextCharge: computed(() => {
            if (props.promotion.funds.recurringCharge) {
                let options: Intl.DateTimeFormatOptions = { timeZone: "UTC", year: "numeric", month: "numeric", day: "numeric" };
                return new Date(props.promotion.funds.recurringCharge.nextCharge).toLocaleDateString(undefined, options);
            }
            return 'N/A';
        }),
        reserved: computed(() => {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.promotion.funds.reservedFunds);
        }),
        remaining: computed(() => {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.promotion.funds.availableFunds);
        })
    }
}

const getMethods = function (props: IProps, data: IData, ctx: any) {
    return {
        toggleEditPromotionModal: function () {
            data.showEditPromotion.value = !data.showEditPromotion.value;
        },
        toggleAddFundsModal: function () {
            data.showAddFunds.value = !data.showAddFunds.value;
        },
        toggleDailyLimitModal: function () {
            data.showDailyLimit.value = !data.showDailyLimit.value;
        },
        toggleRecurringChargeModal: function () {
            data.showRecurringCharge.value = !data.showRecurringCharge.value;
        },
        toggleRemoveFundsModal: function () {
            data.showRemoveFunds.value = !data.showRemoveFunds.value;
        },
        toggleRemovePromotionModal: function () {
            data.showRemovePromotion.value = !data.showRemovePromotion.value;
        },
        locationDisplay: function (locationId: string) {
            var location = data.locations.value.filter(l => {
                return l.id === locationId;
            });

            if (location && location.length == 1) {
                return "" + location[0].name + " (" + location[0].address.city + ", " + location[0].address.regionCode + ")";
            }

            return "";
        },
        approveEditClick: async function () {
            let promotionManager = await context.getService<IPromotionManager>('IPromotionManager');
            var response = await promotionManager.approveEdit(props.promotion.id);
            if (response && response.ok && response.data) {
                showMessage("Changes applied successfully.", "success", true, true);
                ctx.emit(events.updated, response.data);
            } else {
                showMessage("Error applying changes.", "danger", true, true);
            }

        },
        removeEditClick: async function () {
            let promotionManager = await context.getService<IPromotionManager>('IPromotionManager');
            var response = await promotionManager.removeEdit(props.promotion.id);

            if (response && response.ok && response.data) {
                showMessage("Changes removed successfully.", "success", true, true);
                ctx.emit(events.updated, response.data);
            } else {
                showMessage("Error removing changes.", "danger", true, true);
            }
        },

        approvePromo: async function () {
            let promotionManager = await context.getService<IPromotionManager>('IPromotionManager');
            var response = await promotionManager.enable(props.promotion.id);

            if (response && response.ok && response.data) {
                showMessage("Promotion has been activated successfully.", "success", true, true);

                ctx.emit(events.updated, response.data);
            } else {
                showMessage("Error activating promotion.", "danger", true, true);
            }
        },
        cancelEdit: function () {
            data.showEditPromotion.value = false;
        },
        removePromo: function () {
            data.showRemovePromotion.value = false;
            ctx.emit(events.removed, props.promotion);
        },
        updatePromo: function (promotion: PromotionModel) {
            data.showEditPromotion.value = false;
            data.showDailyLimit.value = false;
            data.showAddFunds.value = false;
            data.showRecurringCharge.value = false;
            data.showRemoveFunds.value = false;
            data.showRemovePromotion.value = false;
            ctx.emit(events.updated, promotion);
        }
    }
}

const getLocationDisplays = async function (props: IProps, data: IData) {
    try {
        let locationQueryService = await context.getService<ILocationQueryService>('ILocationQueryService');
        let options = {
            system: "truckdown",
            measureUnit: MeasureUnit.Imperial,
            includeHoursOfOperation: false,
            includeMarketingData: false,
            includeNotes: false,
            includePreference: false,
            accountId: props.promotion.accountId
        } as LocationQueryOptions;

        var response = await locationQueryService.findLocations(props.promotion.locationIds, options);
        if (response) {
            data.locations.value = response.results ?? [];
        }
    } catch (ex) {
        console.log(ex);
    }
}

const events = {
    removed: 'removed',
    updated: 'updated'
}

export default defineComponent({
    name: "PromotionSummary",
    emits: [events.removed, events.updated],
    components: {
        'daily-limit': dailyLimit,
        'remove-funds': removeFunds,
        'add-funds': addFunds,
        'edit-promotion': editPromotion,
        'recurring-charge': recurringCharge,
        'remove-promotion': removePromotion,
        'approve-promotion': approvePromotion
    },
    props: {
        promotion: {
            type: Object as () => PromotionModel,
            required: true
        },
        listing: {
            type: Object as () => Listing,
            required: true
        },
        isModal: {
            type: Boolean,
            required: false,
            default: false
        },
        taxname: {
            type: String,
            required: true
        },
        taxrate: {
            type: Number,
            required: true
        }
    },
    setup: function (props, ctx) {
        let data = getInitialData();
        let computed = getComputed(props, data);
        let methods = getMethods(props, data, ctx);

        onMounted(async () => {
            if (props.promotion.locationIds.length > 0 && !props.promotion.applyToAllLocations) {
                await getLocationDisplays(props, data);
            }
        });

        return {
            ...data,
            ...computed,
            ...methods,
            'isCsrOrAdmin': isCsrOrAdmin(),
            'enableEdit': context.isInRole(Constants.Roles.CustomerAdminRole) || isCsrOrAdmin(),
            'PromotionStatus': PromotionStatus
        };
    }
});
